import React from "react";

const MaterialPanel = ({
  materials,
  subjects,
  subjectContent,
  expandedMaterials,
  loading,
  onToggleMaterial,
  onAddMaterial,
  onEditMaterial,
  onDeleteMaterial,
  onLinkSubjects
}) => {
  if (loading && materials.length === 0) {
    return (
      <div className="col-12">
        <div className="bg-white rounded-12 p-40 text-center">
          <div className="spinner"></div>
          <p className="text-14 text-light-1 mt-20">Loading materials...</p>
        </div>
      </div>
    );
  }

  if (materials.length === 0) {
    return (
      <div className="col-12">
        <div className="bg-white rounded-12 p-40 text-center">
          <i className="icon-book text-60 text-light-3 mb-20"></i>
          <h5 className="text-18 fw-500 mb-10">No Materials Yet</h5>
          <p className="text-14 text-light-1 mb-30">
            Create a material pack and link subjects to it. Each material can have its own pricing.
          </p>
          <button 
            onClick={onAddMaterial}
            className="button -md bg-orange-1 text-white fw-500"
          >
            <i className="icon-plus mr-10"></i>
            Create First Material
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="col-12">
      <div className="bg-white rounded-12 p-20">
        <div className="d-flex items-center justify-between mb-20">
          <div>
            <h5 className="text-16 fw-500">All Materials</h5>
            <p className="text-13 text-light-1 mt-5">
              Create materials and link subjects. Users can purchase materials to access PDFs.
            </p>
          </div>
          <button 
            onClick={onAddMaterial}
            className="button -sm bg-orange-1 text-white"
          >
            <i className="icon-plus mr-8"></i>
            Add Material
          </button>
        </div>

        <div className="materials-list">
          {materials.map((material) => {
            const isExpanded = expandedMaterials.has(material._id);
            // Check reverse relationship: find subjects that are in material's subjects array
            const materialSubjects = subjects.filter(s => 
              (material.subjects || []).some(subjId => (subjId._id || subjId) === s._id)
            );
            const subjectCount = materialSubjects.length;

            return (
              <div key={material._id} className="exam-card bg-light-5 rounded-8 mb-15 border border-light-3">
                <div 
                  className="d-flex items-center justify-between px-20 py-15 cursor-pointer hover:bg-white transition-all rounded-8"
                  onClick={() => onToggleMaterial(material._id)}
                >
                  <div className="d-flex items-center flex-1">
                    <div className="d-flex items-center mr-15">
                      <i className={`text-14 ${isExpanded ? 'icon-chevron-down' : 'icon-chevron-right'} text-light-1 mr-10`}></i>
                      <div 
                        className="size-40 d-flex justify-center items-center rounded-8 mr-15"
                        style={{ backgroundColor: (material.color || '#f59e0b') + '20', color: material.color || '#f59e0b' }}
                      >
                        <i className="icon-book text-16"></i>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="d-flex items-center x-gap-10 mb-5">
                        <h6 className="text-15 fw-500">{material.title}</h6>
                        {material.isFree && (
                          <span className="badge bg-green-1 text-green fw-600" style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '12px' }}>
                            Free
                          </span>
                        )}
                        {material.isPurchasable && !material.isFree && (
                          <div className="d-flex items-center x-gap-5">
                            <span className="fw-700" style={{ fontSize: '16px', color: '#059669', letterSpacing: '0.3px' }}>
                              ₹{material.discountPrice || material.price}
                            </span>
                            {material.discountPrice && material.discountPrice < material.price && (
                              <span className="text-light-3 line-through" style={{ fontSize: '13px' }}>₹{material.price}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="text-12 text-light-1">{material.description || 'No description'}</div>
                      <div className="d-flex items-center x-gap-15 mt-8">
                        <span className="text-11 text-light-2">
                          <i className="icon-book mr-5"></i>
                          {subjectCount} subject{subjectCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Action Menu */}
                  <div className="d-flex items-center" style={{ gap: '10px' }}>
                    <button 
                      onClick={(e) => { e.stopPropagation(); onLinkSubjects(material); }}
                      className="button -sm fw-500"
                      title="Link subjects"
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        fontSize: '13px',
                        minWidth: '40px',
                        backgroundColor: '#3b82f6',
                        color: '#ffffff',
                        boxShadow: '0 1px 3px rgba(59, 130, 246, 0.2)',
                        marginRight: '10px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#2563eb';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#3b82f6';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(59, 130, 246, 0.2)';
                      }}
                    >
                      <i className="fa fa-link" aria-hidden="true" style={{ fontSize: '16px' }}></i>
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); onEditMaterial(material); }}
                      className="button -sm fw-500"
                      title="Edit material"
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        fontSize: '13px',
                        minWidth: '40px',
                        backgroundColor: '#10b981',
                        color: '#ffffff',
                        boxShadow: '0 1px 3px rgba(16, 185, 129, 0.2)',
                        marginRight: '10px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#059669';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#10b981';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(16, 185, 129, 0.2)';
                      }}
                    >
                      <i className="icon-edit"></i>
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); onDeleteMaterial(material._id); }}
                      className="button -sm fw-500"
                      title="Delete material"
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        fontSize: '13px',
                        minWidth: '40px',
                        backgroundColor: '#ef4444',
                        color: '#ffffff',
                        boxShadow: '0 1px 3px rgba(239, 68, 68, 0.2)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#dc2626';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#ef4444';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(239, 68, 68, 0.2)';
                      }}
                    >
                      <i className="icon-bin"></i>
                    </button>
                  </div>
                </div>
                {/* Expanded Content */}
                {isExpanded && (
                  <div className="px-20 pb-20 pt-0">
                    <div className="border-top border-light-3 pt-20 mt-15">
                      {subjectCount === 0 ? (
                        <div className="text-center py-30">
                          <i className="icon-link text-30 text-light-3 mb-10"></i>
                          <p className="text-13 text-light-2 mb-20">
                            No subjects linked yet. Click &quot;Link Subjects&quot; to add subjects with pricing.
                          </p>
                          <button 
                            onClick={(e) => { e.stopPropagation(); onLinkSubjects(material); }}
                            className="button -sm bg-blue-1 text-blue"
                          >
                            <i className="icon-link mr-8"></i>
                            Link Subjects Now
                          </button>
                        </div>
                      ) : (
                        <div>
                          <h6 className="text-14 fw-500 mb-15">Linked Subjects ({subjectCount})</h6>
                          <div 
                            className="subjects-grid"
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                              gap: '15px'
                            }}
                          >
                            {materialSubjects.map((subject) => {
                              const content = subjectContent[subject._id];
                              const videoCount = content?.videos?.length || 0;
                              const materialCount = content?.pdfs?.length || 0;
                              const testCount = content?.mockTests?.length || 0;
                              
                              return (
                                <div 
                                  key={subject._id} 
                                  className="subject-link-card bg-white rounded-8 border border-light-3"
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-start',
                                    padding: '15px',
                                    transition: 'all 0.2s',
                                    cursor: 'pointer'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.backgroundColor = '#ffffff';
                                  }}
                                >
                                  <div 
                                    className="size-30 d-flex justify-center items-center rounded-6 mr-15"
                                    style={{backgroundColor: subject.color + '20', color: subject.color, flexShrink: 0}}
                                  >
                                    <i className={`text-12 ${subject.icon || 'icon-book'}`}></i>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-13 fw-500 mb-3" style={{ color: '#1e293b' }}>{subject.title}</div>
                                    {subject.description && (
                                      <div className="text-11 text-light-2 mb-3" style={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        lineHeight: '1.4'
                                      }}>
                                        {subject.description}
                                      </div>
                                    )}
                                    <div className="mb-3">
                                      <span className="badge bg-yellow-1 text-yellow text-10" style={{ padding: '2px 6px', borderRadius: '8px' }}>
                                        No pricing
                                      </span>
                                    </div>
                                    <div className="d-flex items-center x-gap-10 text-10 text-light-2">
                                      <span>
                                        <i className="icon-play-circle text-blue mr-3"></i>
                                        {videoCount} videos
                                      </span>
                                      <span>
                                        <i className="icon-file-text text-green mr-3"></i>
                                        {materialCount} materials
                                      </span>
                                      <span>
                                        <i className="icon-clipboard-check text-orange mr-3"></i>
                                        {testCount} tests
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MaterialPanel;
